import { mdiChevronLeft, mdiCloseCircleOutline } from '@mdi/js';
import Icon from '@mdi/react';
import {
  appRoutes,
  changePhoneNumberMessages,
  commonButtonMessages,
  insertPhoneNumberUserRegistrationMessages,
  phoneVerificationUserRegistrationMessages,
  useInsertPhoneNumberPageLogic,
  isValidPhone
} from 'azshared';
import React, { FormEvent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AppButton, AppButtonType } from '../../../../components/buttons/app-button';
import { Card } from '../../../../components/card/card';
import { AppDialog } from '../../../../components/dialog/app-dialog';
import { AppInputText } from '../../../../components/input-text/app-input-text';
import { AppLink } from '../../../../components/labels/app-link';
import { useGlobalActions, useGoToLoginFromRegistration } from '../../../../hooks/global-actions';
import { LoginContainer } from '../../login-container/login-container';
import { SearchablePrefixPicker } from '../../../../components/searchable-prefix-picker/searchable-prefix-picker';
import './phone-number-user-registration-page.scss';
import { BannerCookiePolicy } from 'src/components/banner-cookie-policy/banner-cookie-policy';
import { useNavigate } from 'react-router-dom';

export const InsertPhoneNumberUserRegistrationPage = () => {
  const { formatMessage } = useIntl();
  const goToLoginFromRegistration = useGoToLoginFromRegistration();
  const globalActions = useGlobalActions();

  const {
    buttonDisabled,
    phoneNumber,
    insertPhoneNumberRequestStatus,
    insertPhoneNumberError,
    phoneNumberAlreadyExisting,
    handleSubmit,
    updatePhoneNumber,
    handleClose
  } = useInsertPhoneNumberPageLogic(useGlobalActions());

  const navigate = useNavigate();
  const handleClickGoBack = useCallback(() => {
    navigate(appRoutes.InsertEmailUserRegistration);
  }, [navigate]);

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!isValidPhone(phoneNumber)) {
        globalActions.showToast({
          type: 'error',
          title: formatMessage(changePhoneNumberMessages.errorInvalidPhoneNumberFormat),
          close: globalActions.closeToast
        });
        return;
      }
      handleSubmit();
    },
    [formatMessage, globalActions, handleSubmit, phoneNumber]
  );

  return (
    <div>
      <LoginContainer>
        <div className='insert-phone-number-user-registration-page'>
          <Card
            title={formatMessage(insertPhoneNumberUserRegistrationMessages.pageTitle)}
            subtitle={formatMessage(insertPhoneNumberUserRegistrationMessages.pageSubtitle)}
            clickGoBack={handleClickGoBack}
            back={formatMessage(commonButtonMessages.backLink)}>
            <form onSubmit={onSubmit}>
              <div className='phone-number-input-row-container'>
                <label className='phone-number-input-label'>
                  {formatMessage(insertPhoneNumberUserRegistrationMessages.labelInsertPhoneNumberInputText)}
                </label>
                <SearchablePrefixPicker
                  placeholder={formatMessage(insertPhoneNumberUserRegistrationMessages.placeholderInsertPhoneNumberInputText)}
                  value={phoneNumber}
                  onChange={updatePhoneNumber}
                  disabled={phoneNumberAlreadyExisting}
                />
              </div>

              <div className='button'>
                <AppButton
                  label={formatMessage(insertPhoneNumberUserRegistrationMessages.buttonSendCodeByPhone)}
                  type={AppButtonType.SUBMIT}
                  disabled={phoneNumberAlreadyExisting ? false : buttonDisabled}
                  loading={insertPhoneNumberRequestStatus === 'loading'}
                />
              </div>
            </form>
            <div className='description-disclaimer insert-phone-number'>
              {formatMessage(insertPhoneNumberUserRegistrationMessages.descriptionDisclaimerInsertPhoneNumber)}
            </div>
            <div className='disclaimer'>
              {formatMessage(commonButtonMessages.linkBackToLogin)}
              <AppLink to={appRoutes.Login} replace={false} label={formatMessage(commonButtonMessages.linkToLogin)} onClick={goToLoginFromRegistration} />
            </div>
          </Card>
        </div>
      </LoginContainer>
      <AppDialog open={insertPhoneNumberError === 'CELLULARE_NON_UTILIZZABILE'}>
        <div className='dialog-error-phone-number-verification'>
          <Icon path={mdiCloseCircleOutline} size={2} color={'red'} />
          <div className='title-dialog'>{formatMessage(phoneVerificationUserRegistrationMessages.errorPhoneNumberNotUsableTitle)}</div>
          <div className='text-dialog'>{formatMessage(phoneVerificationUserRegistrationMessages.errorPhoneNumberNotUsableMessages)}</div>
          <div className='button-dialog'>
            <AppButton
              type={AppButtonType.BUTTON}
              onClick={handleClose}
              label={formatMessage(phoneVerificationUserRegistrationMessages.errorPhoneNumberNotUsableButtonLabel)}
            />
          </div>
        </div>
      </AppDialog>
      <BannerCookiePolicy />
    </div>
  );
};
